import { CombinedBgBomItem } from './../models/bg.model';
import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { RowInput, Settings } from 'jspdf-autotable';

import { BgBomCategory } from '../models/bg.model';
import { ProjectModel } from '../models/projectModel';
import { AuthService } from './auth.service';
import { normalizeFileName } from '../utils/stringUtils';
import { Rfp, RfpContractor, Labor } from '../models/rfp.model';
import { OfferItem } from '../models/commercial-offer.model';

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private groupColor = '#2980BA';
  private pageHeaderColor = '#0C54A0';
  private footerLinkColor = '#0C54A0';
  private footerNoteColor = '#000000';
  private totalHeaderColor = '#66B0FF';
  private totalHeaderTextColor = '#000000';
  private totalEvenColor = '#E6F2FF';
  private totalOddColor = '#CCE5FF';
  private labelColor = '#555555';
  private bodyTextColor = '#222222';
  private dividerColor = '#E0E0E0';
  private defaultMargin = 40;

  private headerRow = 30;
  private categoryRow = 20;
  private spaceBetweenLines = 5;

  private projectNumberFontSize = 12;
  private projectNameFontSize = 16;
  private bomFontSize = 16;
  private categoryFontSize = 16;
  private clientFontSize = 18;
  private footerFontSize = 8;

  private pxToPtScaleFactor = 72 / 96;

  constructor(
    private authService: AuthService
  ) { }

  exportModelToPdf(
    modelName?: string,
    categories?: Array<BgBomCategory>,
    projectModel?: ProjectModel,
    client?: string,
    download?: boolean
  ) {
    const doc = new jsPDF('l', 'pt', 'a4', true);

    categories
      ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((category) => {
        const tableData: RowInput[] = [];

        let rowNumber = 1;

        category.bgGroupedSubCategories?.forEach((group) => {
          if (group.name?.toUpperCase() !== 'DEFAULT') {
            tableData.push({ rowNumber: group.name ?? '', group: true });
          }
          group.bgSubCategories?.forEach((subGroup) => {
            const isTotal = subGroup.name.toLowerCase().startsWith('total');

            tableData.push({
              rowNumber: subGroup.name,
              subGroup: true,
              totalHeader: isTotal,
            });

            let lastItemName: string | undefined = '';
            let lastItemNameAndSize: string | undefined = '';
            subGroup.bgBomItems.forEach((item) => {
              let currentItemNameAndSize = `${item.itemName}${item.size}`;
              tableData.push({
                rowNumber: rowNumber++,
                itemName: item.itemName === lastItemName && category.name.toLowerCase() === 'framing' ? '' : item.itemName ?? '',
                size: item.size ?? '',
                description: item.description ?? '',
                manufacturer: item.manufacturer ?? '',
                manufacturerModel: item.manufacturerModel ?? '',
                quantity: `${item.modelQuantity ?? ''} ${item.modelQuantityDescription}`,
                wasteFactor: item.wasteFactor ?? '',
                toOrder: `${item.quantityToOrder ?? ''} ${item.quantityToOrderDescription}`,
                total: isTotal,
              });

              lastItemName = item.itemName;
              lastItemNameAndSize = currentItemNameAndSize;
            });
          });
        });

        let lastAutoTableFinalY: number =
          (doc as any).lastAutoTable.finalY ?? 0;
        let lastAutoTableEndPage: number =
          ((doc as any).lastAutoTable.startPageNumber ?? 0) +
          ((doc as any).lastAutoTable.pageCount ?? 0) -
          1;

        doc.setFontSize(this.projectNameFontSize);
        let projectNameRow = doc.getTextDimensions(
          projectModel?.projectName ?? ''
        ).h;

        doc.setFontSize(this.projectNumberFontSize);
        let projectNumberRow = doc.getTextDimensions(
          projectModel?.projectNumber ?? ''
        ).h;

        let numberOfRowsForProjectNumber = (
          projectModel?.projectNumber ?? ''
        ).split('\n').length;

        let marginTop =
          this.headerRow +
          this.spaceBetweenLines +
          (projectNameRow > 0 ? projectNameRow + this.spaceBetweenLines : 0) +
          (projectNumberRow > 0
            ? projectNumberRow * numberOfRowsForProjectNumber +
            this.spaceBetweenLines
            : 0) +
          this.categoryRow +
          this.spaceBetweenLines;

        autoTable(doc, {
          body: tableData,
          startY: Math.max(
            marginTop,
            lastAutoTableFinalY +
            2 * this.spaceBetweenLines +
            this.categoryRow +
            this.spaceBetweenLines
          ),
          margin: { top: marginTop },
          columns: [
            { dataKey: 'rowNumber', title: '#' },
            { dataKey: 'itemName', title: 'Item' },
            { dataKey: 'size', title: 'Size' },
            { dataKey: 'description', title: 'Description' },
            { dataKey: 'manufacturer', title: 'Manufacturer' },
            { dataKey: 'manufacturerModel', title: 'Model' },
            { dataKey: 'quantity', title: 'Quantity' },
            { dataKey: 'wasteFactor', title: 'Waste Factor' },
            { dataKey: 'toOrder', title: 'Quantity To Order' },
          ],
          columnStyles: {
            rowNumber: { halign: 'right' },
            itemName: { halign: 'left' },
            size: { halign: 'left', overflow: 'visible' },
            description: { halign: 'left' },
            manufacturer: { halign: 'left' },
            manufacturerModel: { halign: 'left' },
            quantity: { halign: 'center' },
            wasteFactor: { halign: 'center' },
            toOrder: { halign: 'center' },
          },
          styles: { fontSize: 8, valign: 'middle' },
          theme: 'striped',
          tableWidth: 'auto',
          pageBreak: lastAutoTableEndPage === -1 ? 'auto' : 'avoid', // 'auto' for first table to fix problem with first page get empty in some cases
          didDrawPage: (data) => {
            const currentPage =
              (data.table.startPageNumber ?? 1) + data.table.pageNumber - 1;

            if (currentPage === lastAutoTableEndPage) {
              this.drawCategoryTitle(doc, category, lastAutoTableFinalY);
            } else {
              this.drawHeader(
                doc,
                client,
                category,
                projectModel,
                data.settings,
                projectNameRow,
                projectNumberRow,
                numberOfRowsForProjectNumber
              );

              this.drawFooter(doc, data.settings);
            }
          },
          didParseCell: (data) => {
            if (data.section === 'body') {
              // special styling for groups
              if ((<any>data.row.raw).group === true) {
                data.cell.colSpan = data.table.columns.length;
                data.cell.styles.halign = 'center';
                data.cell.styles.textColor = this.groupColor;
                data.cell.styles.fontStyle = 'bold';
              }
              // special styling for sub-groups
              if ((<any>data.row.raw).subGroup === true) {
                data.cell.colSpan = data.table.columns.length;
                data.cell.styles.halign = 'left';
                data.cell.styles.textColor = this.groupColor;
                data.cell.styles.fontStyle = 'bold';
              }
              // special styling for total sub-groups
              if ((<any>data.row.raw).totalHeader === true) {
                data.cell.styles.fillColor = this.totalHeaderColor;
                data.cell.styles.textColor = this.totalHeaderTextColor;
              }
              if ((<any>data.row.raw).total === true) {
                data.row.index % 2 == 1
                  ? (data.cell.styles.fillColor = this.totalOddColor)
                  : (data.cell.styles.fillColor = this.totalEvenColor);
              }
            }
          },
        });
      });

    let title = modelName?.replace(/\s/, '')?.replace(/[^A-Z0-9]/gi, '_');
    doc.setProperties({
      title: title,
    });

    const filename = normalizeFileName(`${projectModel?.projectName} .pdf`);
    if (download) {
      doc.save(filename);
      return;
    }

    const pdf = doc.output('datauristring');

    if (pdf.length > 2_000_000) {
      // ~2MB
      doc.save(filename);
      return;
    }

    doc.output('dataurlnewwindow', { filename });
  }


  exportModelPriceToPdf(
    modelName?: string,
    categories?: Array<BgBomCategory>,
    projectModel?: ProjectModel,
    client?: string,
    download?: boolean
  ) {
    const doc = new jsPDF('l', 'pt', 'a4', true);

    categories
      ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((category) => {
        const tableData: RowInput[] = [];

        let rowNumber = 1;

        category.bgGroupedSubCategories?.forEach((group) => {
          if (group.name?.toUpperCase() !== 'DEFAULT') {
            tableData.push({ rowNumber: group.name ?? '', group: true });
          }
          group.bgSubCategories?.forEach((subGroup) => {
            const isTotal = subGroup.name.toLowerCase().startsWith('total');

            tableData.push({
              rowNumber: subGroup.name,
              subGroup: true,
              totalHeader: isTotal,
            });

            let lastItemName: string | undefined = '';
            let lastItemNameAndSize: string | undefined = '';
            subGroup.bgBomItems.forEach((item) => {
              let currentItemNameAndSize = `${item.itemName}${item.size}`;
              tableData.push({
                rowNumber: rowNumber++,
                itemName:
                  item.itemName === lastItemName &&
                    category.name.toLowerCase() === 'framing'
                    ? ''
                    : item.itemName ?? '',
                size:
                  item.size ?? '',
                description: item.description ?? '',
                manufacturer: item.manufacturer ?? '',
                manufacturerModel: item.manufacturerModel ?? '',
                quantity: `${item.modelQuantity ?? ''} ${item.modelQuantityDescription}`,
                wasteFactor: item.wasteFactor ?? '',
                toOrder: `${item.quantityToOrder ?? ''} ${item.quantityToOrderDescription}`,
                cost: formatCurrency(item.cost),
                totalCost: formatCurrency(item.totalCost),
                total: isTotal,
              });

              lastItemName = item.itemName;
              lastItemNameAndSize = currentItemNameAndSize;
            });
          });
        });

        //Format to  currency
        function formatCurrency(value: number | undefined): string {
          return value?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          }) ?? '';
        }


        let lastAutoTableFinalY: number =
          (doc as any).lastAutoTable.finalY ?? 0;
        let lastAutoTableEndPage: number =
          ((doc as any).lastAutoTable.startPageNumber ?? 0) +
          ((doc as any).lastAutoTable.pageCount ?? 0) -
          1;

        doc.setFontSize(this.projectNameFontSize);
        let projectNameRow = doc.getTextDimensions(
          projectModel?.projectName ?? ''
        ).h;

        doc.setFontSize(this.projectNumberFontSize);
        let projectNumberRow = doc.getTextDimensions(
          projectModel?.projectNumber ?? ''
        ).h;

        let numberOfRowsForProjectNumber = (
          projectModel?.projectNumber ?? ''
        ).split('\n').length;

        let marginTop =
          this.headerRow +
          this.spaceBetweenLines +
          (projectNameRow > 0 ? projectNameRow + this.spaceBetweenLines : 0) +
          (projectNumberRow > 0
            ? projectNumberRow * numberOfRowsForProjectNumber +
            this.spaceBetweenLines
            : 0) +
          this.categoryRow +
          this.spaceBetweenLines;

        autoTable(doc, {
          body: tableData,
          startY: Math.max(
            marginTop,
            lastAutoTableFinalY +
            2 * this.spaceBetweenLines +
            this.categoryRow +
            this.spaceBetweenLines
          ),
          margin: { top: marginTop },
          columns: [
            { dataKey: 'rowNumber', title: '#' },
            { dataKey: 'itemName', title: 'Item' },
            { dataKey: 'size', title: 'Size' },
            { dataKey: 'description', title: 'Description' },
            { dataKey: 'manufacturer', title: 'Manufacturer' },
            { dataKey: 'manufacturerModel', title: 'Model' },
            { dataKey: 'quantity', title: 'Quantity' },
            { dataKey: 'wasteFactor', title: 'Waste Factor' },
            { dataKey: 'toOrder', title: 'Quantity To Order' },
            { dataKey: 'cost', title: 'Cost' },
            { dataKey: 'totalCost', title: 'Total Cost' },

          ],
          columnStyles: {
            rowNumber: { halign: 'right' },
            itemName: { halign: 'left' },
            size: { halign: 'left', overflow: 'visible' },
            description: { halign: 'left' },
            manufacturer: { halign: 'left' },
            manufacturerModel: { halign: 'left' },
            quantity: { halign: 'center' },
            wasteFactor: { halign: 'center' },
            toOrder: { halign: 'center' },
            cost: { halign: 'right' },
            totalCost: { halign: 'right' },
          },
          styles: { fontSize: 8, valign: 'middle' },
          theme: 'striped',
          tableWidth: 'auto',
          pageBreak: lastAutoTableEndPage === -1 ? 'auto' : 'avoid', // 'auto' for first table to fix problem with first page get empty in some cases
          didDrawPage: (data) => {
            const currentPage =
              (data.table.startPageNumber ?? 1) + data.table.pageNumber - 1;

            if (currentPage === lastAutoTableEndPage) {
              this.drawCategoryTitle(doc, category, lastAutoTableFinalY);
            } else {
              this.drawHeader(
                doc,
                client,
                category,
                projectModel,
                data.settings,
                projectNameRow,
                projectNumberRow,
                numberOfRowsForProjectNumber
              );

              this.drawFooter(doc, data.settings);
            }
          },
          didParseCell: (data) => {
            if (data.section === 'body') {
              // special styling for groups
              if ((<any>data.row.raw).group === true) {
                data.cell.colSpan = data.table.columns.length;
                data.cell.styles.halign = 'center';
                data.cell.styles.textColor = this.groupColor;
                data.cell.styles.fontStyle = 'bold';
              }
              // special styling for sub-groups
              if ((<any>data.row.raw).subGroup === true) {
                data.cell.colSpan = data.table.columns.length;
                data.cell.styles.halign = 'left';
                data.cell.styles.textColor = this.groupColor;
                data.cell.styles.fontStyle = 'bold';
              }
              // special styling for total sub-groups
              if ((<any>data.row.raw).totalHeader === true) {
                data.cell.styles.fillColor = this.totalHeaderColor;
                data.cell.styles.textColor = this.totalHeaderTextColor;
              }
              if ((<any>data.row.raw).total === true) {
                data.row.index % 2 == 1
                  ? (data.cell.styles.fillColor = this.totalOddColor)
                  : (data.cell.styles.fillColor = this.totalEvenColor);
              }
            }
          },
        });
      });

    let title = modelName?.replace(/\s/, '')?.replace(/[^A-Z0-9]/gi, '_');
    doc.setProperties({
      title: title,
    });

    const filename = normalizeFileName(`${projectModel?.projectName} .pdf`);
    if (download) {
      doc.save(filename);
      return;
    }

    const pdf = doc.output('datauristring');

    if (pdf.length > 2_000_000) {
      // ~2MB
      doc.save(filename);
      return;
    }

    doc.output('dataurlnewwindow', { filename });
  }


  exportModelToPdfComparsion(
    modelName?: string,
    categories?: Array<BgBomCategory>,
    projectModel?: ProjectModel,
    client?: string,
    download?: boolean
  ) {
    this.exportDynamicModelToPdf(modelName, categories, projectModel, client, download);
    return;
  }


  exportDynamicModelToPdf(
    modelName?: string,
    categories?: Array<BgBomCategory>,
    projectModel?: ProjectModel,
    client?: string,
    download?: boolean
  ) {
    const doc = new jsPDF('l', 'pt', 'a4', true);

    // Step 1: Collect Dynamic Columns
    const quantityKeysSet = new Set<string>();
    const keyMapping: { [simplifiedKey: string]: string } = {};

    categories?.forEach((BgBomCategory) => {
      BgBomCategory?.bgGroupedSubCategories?.forEach((groupSubCategory) => {
        groupSubCategory?.bgSubCategories?.forEach((subCategory) => {
          subCategory.combinedBgBomItems?.forEach((item: CombinedBgBomItem) => {
            if (item.combinedQuantityToOrder) {
              Object.keys(item.combinedQuantityToOrder).forEach((originalKey: string) => {
                const parts = originalKey.split('_');
                const simplifiedKey = parts.slice(-2).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
                quantityKeysSet.add(simplifiedKey);
                keyMapping[simplifiedKey] = originalKey;
              });
            }
          });
        });
      });
    });

    const combinedQuantityKeys = Array.from(quantityKeysSet);

    // Define displayed columns including dynamic columns
    const displayedColumns = [
      'itemName',
      'size',
      'description',
      'modelQuantity',
      'modelQuantityDescription',
      ...combinedQuantityKeys,
    ];

    categories?.forEach((category) => {
      const tableData: RowInput[] = [];
      let rowNumber = 1;

      category.bgGroupedSubCategories?.forEach((group) => {
        if (group.name?.toUpperCase() !== 'DEFAULT') {
          tableData.push({ rowNumber: group.name ?? '', group: true });
        }
        group.bgSubCategories?.forEach((subGroup) => {
          const isTotal = subGroup.name.toLowerCase().startsWith('total');

          tableData.push({
            rowNumber: subGroup.name,
            subGroup: true,
            totalHeader: isTotal,
          });

          subGroup.combinedBgBomItems?.forEach((item: CombinedBgBomItem) => {
            const dataRow: any = {
              rowNumber: rowNumber++,
              itemName: item.itemName ?? '',
              size: item.size ?? '',
              description: item.description ?? '',
              modelQuantity: item.modelQuantity ?? '',
              modelQuantityDescription: item.modelQuantityDescription ?? '',
            };

            // Add dynamic quantities
            combinedQuantityKeys.forEach((key) => {
              const originalKey = keyMapping[key];
              dataRow[key] = item.combinedQuantityToOrder?.[originalKey] ?? '';
            });

            tableData.push(dataRow);
          });
        });
      });

      let lastAutoTableFinalY: number = (doc as any).lastAutoTable?.finalY ?? 0;
      let lastAutoTableEndPage: number =
        ((doc as any).lastAutoTable?.startPageNumber ?? 0) +
        ((doc as any).lastAutoTable?.pageCount ?? 0) -
        1;

      doc.setFontSize(this.projectNameFontSize);
      let projectNameRow = doc.getTextDimensions(projectModel?.projectName ?? '').h;

      doc.setFontSize(this.projectNumberFontSize);
      let projectNumberRow = doc.getTextDimensions(projectModel?.projectNumber ?? '').h;

      let numberOfRowsForProjectNumber = (projectModel?.projectNumber ?? '').split('\n').length;

      let marginTop =
        this.headerRow +
        this.spaceBetweenLines +
        (projectNameRow > 0 ? projectNameRow + this.spaceBetweenLines : 0) +
        (projectNumberRow > 0
          ? projectNumberRow * numberOfRowsForProjectNumber + this.spaceBetweenLines
          : 0) +
        this.categoryRow +
        this.spaceBetweenLines;

      // Prepare columns for autoTable
      const columns = [
        { dataKey: 'rowNumber', title: '#' },
        { dataKey: 'itemName', title: 'Item' },
        { dataKey: 'size', title: 'Size' },
        { dataKey: 'description', title: 'Description' },
        { dataKey: 'modelQuantity', title: 'Model Quantity' },
        { dataKey: 'modelQuantityDescription', title: 'Units of Measurement' },
        // Add dynamic columns
        ...combinedQuantityKeys.map((key) => ({ dataKey: key, title: key })),
      ];

      autoTable(doc, {
        body: tableData,
        startY: Math.max(
          marginTop,
          lastAutoTableFinalY + 2 * this.spaceBetweenLines + this.categoryRow + this.spaceBetweenLines
        ),
        margin: { top: marginTop },
        columns: columns,
        columnStyles: {
          rowNumber: { halign: 'right' },
          itemName: { halign: 'left' },
          size: { halign: 'left', overflow: 'visible' },
          description: { halign: 'left' },
          modelQuantity: { halign: 'center' },
          modelQuantityDescription: { halign: 'center' },
          // Dynamic columns styles
          ...combinedQuantityKeys.reduce((acc, key) => {
            acc[key] = { halign: 'center' };
            return acc;
          }, {} as any),
        },
        styles: { fontSize: 8, valign: 'middle' },
        theme: 'striped',
        tableWidth: 'auto',
        pageBreak: lastAutoTableEndPage === -1 ? 'auto' : 'avoid',
        didDrawPage: (data) => {
          const currentPage = (data.table.startPageNumber ?? 1) + data.table.pageNumber - 1;

          if (currentPage === lastAutoTableEndPage) {
            this.drawCategoryTitle(doc, category, lastAutoTableFinalY);
          } else {
            this.drawHeader(
              doc,
              client,
              category,
              projectModel,
              data.settings,
              projectNameRow,
              projectNumberRow,
              numberOfRowsForProjectNumber
            );

            this.drawFooter(doc, data.settings);
          }
        },
        didParseCell: (data) => {
          if (data.section === 'body') {
            if ((<any>data.row.raw).group === true) {
              data.cell.colSpan = data.table.columns.length;
              data.cell.styles.halign = 'center';
              data.cell.styles.textColor = this.groupColor;
              data.cell.styles.fontStyle = 'bold';
            }
            if ((<any>data.row.raw).subGroup === true) {
              data.cell.colSpan = data.table.columns.length;
              data.cell.styles.halign = 'left';
              data.cell.styles.textColor = this.groupColor;
              data.cell.styles.fontStyle = 'bold';
            }
            if ((<any>data.row.raw).totalHeader === true) {
              data.cell.styles.fillColor = this.totalHeaderColor;
              data.cell.styles.textColor = this.totalHeaderTextColor;
            }
            if ((<any>data.row.raw).total === true) {
              data.row.index % 2 == 1
                ? (data.cell.styles.fillColor = this.totalOddColor)
                : (data.cell.styles.fillColor = this.totalEvenColor);
            }
          }
        },
      });
    });

    let title = modelName?.replace(/\s/, '')?.replace(/[^A-Z0-9]/gi, '_');
    doc.setProperties({
      title: title,
    });

    const filename = normalizeFileName(`${projectModel?.projectName}.pdf`);
    if (download) {
      doc.save(filename);
      return;
    }

    const pdf = doc.output('datauristring');

    if (pdf.length > 2_000_000) {
      doc.save(filename);
      return;
    }

    doc.output('dataurlnewwindow', { filename });
  }


  private hexToRgb(hex: string): [number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [224, 224, 224];
  }

  private wrapText(text: string, maxChars: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    words.forEach((word) => {
      if ((currentLine + ' ' + word).trim().length > maxChars && currentLine.length > 0) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine = currentLine ? currentLine + ' ' + word : word;
      }
    });
    if (currentLine.trim().length > 0) lines.push(currentLine.trim());
    return lines.length > 0 ? lines : [''];
  }

  private formatCombinedQuantityToOrder(
    combinedQuantityToOrder: { [key: string]: string } | undefined
  ): string {
    if (!combinedQuantityToOrder) {
      return '';
    }



    return Object.entries(combinedQuantityToOrder)
      .map(([key, value]) => `${key.split('_').slice(-2).join(' ')}: ${value}`)
      .join(', \n ');
  }

  /**
   * Gets all unique keys from combinedQuantityToOrder across a list of CombinedBgBomItem objects
   * @param items Array of CombinedBgBomItem objects
   * @returns Array of unique keys from all combinedQuantityToOrder properties
   */
  getAllCombinedQuantityToOrderKeys(items: CombinedBgBomItem[]): string[] {
    const keysSet = new Set<string>();

    items.forEach((item) => {
      if (item.combinedQuantityToOrder) {
        Object.keys(item.combinedQuantityToOrder).forEach((key) => {
          keysSet.add(key);
        });
      }
    });

    return Array.from(keysSet);
  }






  exportContractorProposalToPdf(
    rfp: Rfp,
    contractor: RfpContractor,
    materialItems: OfferItem[],
    projectName?: string,
    download?: boolean
  ): void {
    const doc = new jsPDF('p', 'pt', 'a4', true);
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const marginLeft = this.defaultMargin;
    const marginRight = this.defaultMargin;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const maxHeaderLineChars = 50;
    const proposal = contractor.proposal;
    if (!proposal) return;

    const laborTotalDuration = proposal.laborTotalDuration;
    const showLaborDuration = laborTotalDuration != null && laborTotalDuration !== 0;
    const labors = proposal.labors ?? [];

    const formatPrice = (value: number | null | undefined): string => {
      const n = value ?? 0;
      const formatted = `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return formatted === '$0.00' ? '-' : formatted;
    };

    const formatDuration = (value: number | null | undefined): string => {
      const n = value ?? 0;
      const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      return parseFloat(formatted.replace(/,/g, '')) === 0 ? '-' : formatted;
    };

    const loggedInClient = this.authService.getClientDataStructure();
    const today = new Date().toLocaleDateString();

    // ─── Helper: draw proposal header ───
    const drawProposalHeader = (startY: number): number => {
      let y = startY;

      // Logo
      const showLogo = loggedInClient?.configuration?.customLogo !== '';
      if (showLogo) {
        try {
          const logoImage = new Image();
          const logoSrc = loggedInClient?.configuration?.customLogo
            ? loggedInClient.configuration.customLogo
            : 'assets/img/pdf-logo.png';
          logoImage.src = logoSrc;
          const logoProps = doc.getImageProperties(logoImage);
          const logoScale = 0.25;
          const logoW = logoProps.width * this.pxToPtScaleFactor * logoScale;
          const logoH = logoProps.height * this.pxToPtScaleFactor * logoScale;
          doc.addImage(
            logoImage, logoProps.fileType,
            marginLeft, y - logoH + 5,
            logoW, logoH,
            'logo', 'FAST'
          );
          y += 5;
        } catch { /* logo not available */ }
      }

      // Title — max 50 chars per line
      doc.setFontSize(18);
      doc.setTextColor(this.pageHeaderColor);
      const titleLines = this.wrapText('CONTRACTOR PROPOSAL', maxHeaderLineChars);
      titleLines.forEach((line) => {
        doc.text(line, pageWidth / 2, y, { align: 'center' });
        y += 20;
      });

      // Date
      doc.setFontSize(9);
      doc.setTextColor(this.labelColor);
      doc.text(`Date: ${today}`, pageWidth - marginRight, y, { align: 'right' });
      y += 18;

      return y;
    };

    // ─────────────────────────────────────
    // PAGE CONTENT
    // ─────────────────────────────────────

    let cursorY = 30;
    cursorY = drawProposalHeader(cursorY);

    // ── Contact & Project Info (key-value with dividers) ──
    const labelWidth = 75;
    const infoLeft = marginLeft;
    const valueLeft = marginLeft + labelWidth;
    const infoRight = pageWidth - marginRight;

    const drawInfoRow = (label: string, value: string) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(this.labelColor);
      doc.text(`${label}:`, infoLeft, cursorY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(this.bodyTextColor);
      const valueLines = this.wrapText(value, 60);
      valueLines.forEach((line) => {
        doc.text(line, valueLeft, cursorY);
        cursorY += 12;
      });
      if (valueLines.length <= 1) cursorY += 6;
      const [r, g, b] = this.hexToRgb(this.dividerColor);
      doc.setDrawColor(r, g, b);
      doc.setLineWidth(0.5);
      doc.line(infoLeft, cursorY - 2, infoRight, cursorY - 2);
      cursorY += 10;
    };

    drawInfoRow('Company', contractor.companyName ?? '—');
    drawInfoRow('Email', contractor.email ?? '—');
    drawInfoRow('Project Name', projectName ?? '—');
    drawInfoRow('Request Title', rfp.name ?? '—');
    if (showLaborDuration && labors.length === 0) {
      drawInfoRow('Labor Total Duration', `${laborTotalDuration} days`);
    }

    // ESTIMATED PRICE (centered, bold, black)
    const totalPrice = proposal.totalPrice ?? 0;
    cursorY += 20;
    doc.setFontSize(18);
    doc.setTextColor(this.pageHeaderColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`ESTIMATED PRICE: ${formatPrice(totalPrice)}`, pageWidth / 2, cursorY, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    cursorY += 18;

    cursorY += 10;

    // ── Materials Table ──
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(this.pageHeaderColor);
    doc.text('Materials', marginLeft, cursorY);
    cursorY += 5;

    const materialTableData = materialItems.map((item, i) => [
      i + 1,
      item.description ?? '',
      `${item.quantity ?? ''} ${item.quantityDescription ?? ''}`.trim(),
      formatPrice(item.totalPrice),
    ]);

    const materialsTotal = proposal.materialTotalPrice ?? 0;

    // Shared column widths: Name/Description, Quantity/Duration, Price (total = contentWidth)
    const colWidths = {
      index: 35,
      nameOrDescription: contentWidth - 35 - 95 - 95,
      quantityOrDuration: 95,
      price: 95,
    };

    autoTable(doc, {
      startY: cursorY,
      head: [['#', 'Description', 'Quantity', 'Price']],
      body: materialTableData,
      foot: [[
        { content: 'Materials Total', colSpan: 3, styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: formatPrice(materialsTotal), styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
      ]],
      margin: { left: marginLeft, right: marginRight, top: 60 },
      tableWidth: contentWidth,
      styles: { fontSize: 8, valign: 'middle', cellPadding: 4 },
      headStyles: { fillColor: this.pageHeaderColor, textColor: '#FFFFFF', fontStyle: 'bold' },
      footStyles: { fillColor: this.totalHeaderColor, textColor: '#000000' },
      theme: 'striped',
      columnStyles: {
        0: { halign: 'center', cellWidth: colWidths.index },
        1: { halign: 'left', cellWidth: colWidths.nameOrDescription },
        2: { halign: 'center', cellWidth: colWidths.quantityOrDuration },
        3: { halign: 'right', cellWidth: colWidths.price },
      },
      didDrawPage: (data) => {
        const settings = data.settings;
        this.drawFooter(doc, settings);
      },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 20;

    // ── Labor Table ──
    if (labors.length > 0) {
      // Check page space
      if (cursorY > pageHeight - 200) {
        doc.addPage();
        cursorY = 60;
      }

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(this.pageHeaderColor);
      doc.text('Labor', marginLeft, cursorY);
      cursorY += 5;

      const laborTableData = labors.map((labor: Labor, i: number) => [
        i + 1,
        labor.name ?? '',
        formatDuration(labor.duration),
        formatPrice(labor.price),
      ]);

      const laborTotal = proposal.laborTotalPrice ?? 0;
      const laborTotalLabel = showLaborDuration ? `Labor Total (${laborTotalDuration} days)` : 'Labor Total';

      autoTable(doc, {
        startY: cursorY,
        head: [['#', 'Description', 'Duration (days)', 'Price']],
        body: laborTableData,
        foot: [[
          { content: laborTotalLabel, colSpan: 3, styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
          { content: formatPrice(laborTotal), styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        ]],
        margin: { left: marginLeft, right: marginRight, top: 60 },
        tableWidth: contentWidth,
        styles: { fontSize: 8, valign: 'middle', cellPadding: 4 },
        headStyles: { fillColor: this.pageHeaderColor, textColor: '#FFFFFF', fontStyle: 'bold' },
        footStyles: { fillColor: this.totalHeaderColor, textColor: '#000000' },
        theme: 'striped',
        columnStyles: {
          0: { halign: 'center', cellWidth: colWidths.index },
          1: { halign: 'left', cellWidth: colWidths.nameOrDescription },
          2: { halign: 'center', cellWidth: colWidths.quantityOrDuration },
          3: { halign: 'right', cellWidth: colWidths.price },
        },
        didDrawPage: (data) => {
          const settings = data.settings;
          this.drawFooter(doc, settings);
        },
      });

      cursorY = (doc as any).lastAutoTable.finalY + 20;
    }

    // ── Notes Section ──
    if (cursorY > pageHeight - 150) {
      doc.addPage();
      cursorY = 60;

    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(this.pageHeaderColor);
    doc.text('Notes', marginLeft, cursorY);
    cursorY += 14;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(this.bodyTextColor);
    const notes = proposal.notes || 'No notes provided.';
    const noteLines = doc.splitTextToSize(notes, contentWidth);
    doc.text(noteLines, marginLeft, cursorY);
    cursorY += noteLines.length * 12 + 15;

    // ─── Save / Open ───
    const contractorName = (contractor.companyName || contractor.email || 'proposal').replace(/\s+/g, '_');
    const filename = normalizeFileName(`${rfp.name}_${contractorName}_proposal.pdf`);

    doc.setProperties({ title: `${rfp.name} - Contractor Proposal` });

    if (download) {
      doc.save(filename);
      return;
    }

    const pdf = doc.output('datauristring');
    if (pdf.length > 2_000_000) {
      doc.save(filename);
      return;
    }
    doc.output('dataurlnewwindow', { filename });
  }


  private drawCategoryTitle(
    doc: jsPDF,
    category: BgBomCategory,
    lastAutoTableFinalY: number
  ) {
    doc.setFontSize(this.categoryFontSize);
    doc.setTextColor(this.pageHeaderColor);

    doc.text(
      category.name.toUpperCase(),
      doc.internal.pageSize.width / 2,
      lastAutoTableFinalY + 2 * this.spaceBetweenLines + this.categoryRow,
      {
        align: 'center',
      }
    );
  }

  private drawHeader(
    doc: jsPDF,
    client: string | undefined,
    category: BgBomCategory,
    projectModel: ProjectModel | undefined,
    settings: Settings,
    projectNameRow: number,
    projectNumberRow: number,
    numberOfRowsForProjectNumber: number
  ) {
    doc.setTextColor(this.pageHeaderColor);

    let startTextY = this.headerRow;
    doc.setFontSize(this.clientFontSize);
    doc.text(client?.toUpperCase() ?? '', settings.margin.left, startTextY, {
      align: 'left',
    });

    doc.setFontSize(this.bomFontSize);
    doc.text('BILL OF MATERIALS', doc.internal.pageSize.width / 2, startTextY, {
      align: 'center',
    });

    const loggedInClient = this.authService.getClientDataStructure();

    // if customLogo explicitly set to empty string in the DB - don't show logo at all
    const showLogo = loggedInClient?.configuration?.customLogo !== "";
    if (showLogo) {
      let logoImage = new Image();
      const logoSrc = loggedInClient?.configuration?.customLogo ? loggedInClient?.configuration?.customLogo : 'assets/img/pdf-logo.png';
      //  const logoSrc =  'assets/img/pdf-logo.png';

      logoImage.src = logoSrc;

      let logoImageProperties = doc.getImageProperties(logoImage);
      let logoImageScale = 0.3;
      let logoImageWidth =
        logoImageProperties.width * this.pxToPtScaleFactor * logoImageScale;
      let logoImageHeight =
        logoImageProperties.height * this.pxToPtScaleFactor * logoImageScale;

      doc.addImage(
        logoImage,
        logoImageProperties.fileType,
        doc.internal.pageSize.width - logoImageWidth - settings.margin.left,
        startTextY - logoImageHeight,
        logoImageWidth,
        logoImageHeight,
        'logo',
        'FAST'
      );
    }

    startTextY += this.spaceBetweenLines;
    if (projectModel?.projectName) {
      startTextY += projectNameRow;

      doc.setFontSize(this.projectNameFontSize);
      doc.text(
        projectModel?.projectName ?? '',
        settings.margin.left,
        startTextY,
        {
          align: 'left',
        }
      );
      startTextY += this.spaceBetweenLines;
    }

    if (projectModel?.projectNumber) {
      startTextY += projectNumberRow;

      doc.setFontSize(this.projectNumberFontSize);
      doc.text(
        projectModel?.projectNumber ?? '',
        settings.margin.left,
        startTextY,
        {
          align: 'left',
        }
      );

      startTextY += projectNumberRow * (numberOfRowsForProjectNumber - 1);
      startTextY += this.spaceBetweenLines;
    }

    startTextY += this.categoryRow;

    doc.setFontSize(this.categoryFontSize);
    doc.text(
      category.name.toUpperCase(),
      doc.internal.pageSize.width / 2,
      startTextY,
      {
        align: 'center',
      }
    );
  }

  private drawFooter(doc: jsPDF, settings: Settings) {
    let today = new Date();
    let todayString = today.toLocaleDateString();

    doc.setTextColor(this.footerNoteColor);

    doc.setFontSize(this.footerFontSize);
    doc.text(
      'NOTE: Quantities and waste factors to be verified by Suppliers and Trade Partners',
      settings.margin.left,
      doc.internal.pageSize.height - 30,
      { align: 'left' }
    );

    doc.text(
      `${doc.getCurrentPageInfo().pageNumber}`,
      doc.internal.pageSize.width - settings.margin.left,
      doc.internal.pageSize.height - 30,
      { align: 'right' }
    );

    doc.text(
      todayString,
      doc.internal.pageSize.width - settings.margin.left - 30,
      doc.internal.pageSize.height - 30,
      { align: 'right' }
    );

    const loggedInClient = this.authService.getClientDataStructure();

    // if customWebSite explicitly set to empty string in the DB - don't show website at all
    const showWebSite = loggedInClient?.configuration?.customWebSite !== "";
    if (showWebSite) {
      const urlSrc = loggedInClient?.configuration?.customWebSite ? loggedInClient?.configuration?.customWebSite : 'www.builtgenix.com';
      doc.setTextColor(this.footerLinkColor);
      doc.textWithLink(
        urlSrc,
        settings.margin.left,
        doc.internal.pageSize.height - 20,
        { align: 'left' }
      );
    }

    // if customEmail explicitly set to empty string in the DB - don't show email at all
    const showEmail = loggedInClient?.configuration?.customEmail !== "";
    if (showEmail) {
      const emailSrc = loggedInClient?.configuration?.customEmail ? loggedInClient?.configuration?.customEmail : 'contacts@builtgenix.com';
      doc.textWithLink(
        emailSrc,
        doc.internal.pageSize.width - settings.margin.left,
        doc.internal.pageSize.height - 20,
        { align: 'right' }
      );
    }
  }


}
